import { useState } from "react";
import Button from "../../components/Button";
import ProfileListItem from "./components/ProfileListItem";
import SwitchButton from "../../components/SwitchButton";
import { data } from "./assets/data";
import { Link } from "react-router-dom";
import { LogoutIcon } from "@heroicons/react/outline";
import { useDispatch, useSelector } from "react-redux";
import { logout, updateCurrentUserPreferences } from "../../redux/user/userThunk";
import { selectUser } from "../../redux/user/userSlice";
import Loader from "../../components/Loader";
import { selectLoading, setLoading } from "../../redux/loader/loaderSlice";
import { toast } from "react-hot-toast";
import { useMediaQuery } from "react-responsive";

const NUTRITION_INFO_PREFERENCE_KEY = "wwl_show_nutrition_info";

const getSavedNutritionInfoPreference = () => {
  return localStorage.getItem(NUTRITION_INFO_PREFERENCE_KEY) === "true";
};

export default function ProfileSettings() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const loading = useSelector(selectLoading);

  const isSmallScreen = useMediaQuery({ query: "(max-width: 639px)" });

  const [showNutritionInfo, setShowNutritionInfo] = useState(() =>
    getSavedNutritionInfoPreference()
  );

  const handleNutritionInfoPreferenceChange = (checked) => {
    setShowNutritionInfo(checked);

    localStorage.setItem(
      NUTRITION_INFO_PREFERENCE_KEY,
      checked ? "true" : "false"
    );

    window.dispatchEvent(new Event("nutritionInfoPreferenceChanged"));
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    dispatch(setLoading(true));

    const payload = {
      unit_preference: user.unitPreference,
      primary_diet: user.primaryDiet,
      is_gluten_free: user.isGlutenFree,
      is_dairy_free: user.isDairyFree,
      userId: user.currentUser._id,
    };

    try {
      await dispatch(updateCurrentUserPreferences(payload));
      toast.success("Preferences updated successfully");
    } catch (err) {
      toast.error("Failed to update preferences");
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="m-1 bg-white rounded-xl p-5 mt-10">
      <p className="font-inter text-wwlBlack text-center my-5 text-lg">
        Change your email, password, credit card info, or membership settings here.
      </p>

      <div className="flex justify-center flex-col sm:flex-row items-center gap-3">
        <a
          href={process.env.REACT_APP_MEMBERFUL_REDIRECT_URL}
          target={"_blank"}
          rel={"noreferrer"}
          className="px-4 py-2 inline-block text-white font-inter bg-wwlOrange hover:text-wwlOrange hover:bg-wwlWhite hover:border-wwlOrange rounded-lg border border-transparent w-[146px] sm:w-fit text-center text-sm"
        >
          Manage Account
        </a>

        <Button
          btnFilled={true}
          iconExtraClasses="inline hover:text-wwlOrange"
          btnText={"Logout"}
          BtnIcon={LogoutIcon}
          smallButton={isSmallScreen ? false : true}
          extraClasses="text-white font-inter bg-wwlOrange rounded-lg border border-wwlOrange w-[146px] sm:w-fit text-center"
          onClick={() => dispatch(logout())}
        />

        <a
          href={`${process.env.REACT_APP_FEEDBACK_URL}`}
          target={"_blank"}
          rel={"noreferrer"}
          className="px-4 py-2 inline-block text-white font-inter bg-wwlOrange hover:text-wwlOrange hover:bg-wwlWhite hover:border-wwlOrange rounded-lg border border-transparent block w-[146px] sm:w-fit text-center text-sm"
        >
          Feedback
        </a>

        <div className="order-1 md:order-2">
          <Button
            btnText="Save"
            btnFilled={true}
            smallButton={isSmallScreen ? true : false}
            extraClasses="text-white font-inter bg-wwlOrange rounded-lg border border-wwlOrange w-[146px] sm:w-fit text-center"
            onClick={handleSavePreferences}
          />
        </div>
      </div>

      <form onSubmit={handleSavePreferences}>
        {data?.map((item, index) => (
          <ProfileListItem key={index} data={item} user={user} />
        ))}

        <div className="flex flex-col sm:flex-row justify-between items-start p-5 border-b border-wwlGreen">
          <div>
            <h2 className="font-chivo font-bold text-xl">Nutrition info</h2>
            <p className="font-inter text-sm my-2 lg:text-base text-wwlGray500 max-w-md pr-6">
              Show estimated nutrition info on recipe pages.
            </p>
          </div>

          <div className="flex flex-col gap-2 w-40 mt-3 sm:mt-0">
            <SwitchButton
              customizedOption="Nutrition info"
              isSmall={true}
              userPreference={showNutritionInfo}
              updateReduxState={false}
              changeHandler={(checked) =>
                handleNutritionInfoPreferenceChange(checked)
              }
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center md:justify-between items-center mt-6 px-5 gap-3">
          <Link
            to={"/faq"}
            className="font-semibold font-xs text-wwlGray700 hover:text-wwlOrange transition duration-150 ease-out hover:ease-in max-w-sm order-1 text-center md:text-left"
          >
            Have a question, or looking for prepping advice? Check Out our FAQ
          </Link>
        </div>
      </form>
    </div>
  );
}